import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { EDisplayConditionType, EEqualityType } from '@common/enums/display-condition.enum';
import type { BlockDisplayConditionItem } from '@web/types/structure/structure.type';
import type { Observable } from 'rxjs';
import { forkJoin, map, mergeMap, take } from 'rxjs';
import { PagesFacade } from '@web/store/website/pages/pages.facade';
import { RouterFacade } from '@web/store/router/router.facade';
import { type EduCourseWithTariffs } from '@common/types/edu-course.type';
import { EduCoursesCollectionFacade } from '@web/store/collections/edu-courses/edu-courses.facade';
import { AppFacade } from '@web/store/app/app.facade';
import { EduCoursesHttpService } from '@web/store/collections/edu-courses/edu-courses.http.service';
import { dayjs } from '@common/helpers/dayjs';
import { AsyncPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { WebDatePickerComponent } from '@web/shared/components/web-date-picker/web-date-picker.component';
import { WebTimePickerComponent } from '@web/shared/components/web-time-picker/web-time-picker.component';
import { NgSelectModule } from '@ng-select/ng-select';

type ConditionModel = { conditionType: EDisplayConditionType; equality: EEqualityType; value: any };

const startOfDay = (date: Date): Date => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
};

const endOfDay = (date: Date): Date => {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d;
};

const tomorrow = (): Date => new Date(new Date().setDate(new Date().getDate() + 1));

@Component({
    selector: 'web-condition-item-settings',
    templateUrl: './condition-item-settings.component.html',
    styleUrls: ['./condition-item-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    // `FormsModule` is for `ng-select` alone — it is a ControlValueAccessor and has no other binding.
    imports: [AsyncPipe, TranslateModule, FormsModule, WebDatePickerComponent, WebTimePickerComponent, NgSelectModule],
})
export class ConditionItemSettingsComponent implements OnInit {
    public readonly conditionItem = input.required<BlockDisplayConditionItem>();
    public readonly conditionItemIndex = input<number>(0);
    public readonly isCanRemove = input<boolean>(false);
    public readonly maUserTimezone = input<string | null>(null);
    public readonly valueChangeEvent = output<BlockDisplayConditionItem>();
    public readonly removeConditionItemEvent = output<number>();
    public readonly setOnlyAlwaysCondition = output<void>();

    private readonly pagesFacade = inject(PagesFacade);
    private readonly routerFacade = inject(RouterFacade);
    private readonly eduCourseFacade = inject(EduCoursesCollectionFacade);
    private readonly appFacade = inject(AppFacade);
    private readonly eduCoursesHttpService = inject(EduCoursesHttpService);
    private readonly destroyRef = inject(DestroyRef);

    public readonly isEduSite$: Observable<boolean> = this.pagesFacade.isEduSite$;

    public readonly conditionTypeOptions = Object.values(EDisplayConditionType);
    public readonly equalityOptions = Object.values(EEqualityType);
    public readonly equalityTwoOptions = this.equalityOptions.slice(0, 2); // Only 'Equal' and 'Not Equal'
    public readonly authEduOptions = ['login', 'logout'];

    public readonly EDisplayConditionType = EDisplayConditionType;

    private readonly model = signal<ConditionModel>({ conditionType: null, equality: null, value: null });

    public readonly conditionType = computed(() => this.model().conditionType);
    public readonly equality = computed(() => this.model().equality);
    public readonly value = computed(() => this.model().value);

    public readonly defaultStartDate = signal<Date>(startOfDay(new Date()));
    public readonly defaultEndDate = signal<Date>(endOfDay(tomorrow()));
    public readonly startTime = signal<dayjs.Dayjs | null>(null);
    public readonly endTime = signal<dayjs.Dayjs | null>(null);

    private defaultConditionDateRange: Date[] = [];
    private defaultConditionTimeRange: string[] = [];

    public readonly eduCourses$ = this.appFacade.isAdminCollectionWrapper$(this.getEduCourses$());
    private firstGroup: any;

    public ngOnInit(): void {
        this.eduCourses$
            .pipe(
                take(1),
                map((courses) => courses?.[0])
            )
            .subscribe((value) => {
                this.firstGroup = value;
            });

        const item = this.conditionItem() as ConditionModel;
        const timezone = this.maUserTimezone();
        const value: any = item.value;
        const dateRange = value?.dateRange || [];
        const [startTime, endTime] = value?.timeRange || [];

        const defaultStartTime = dayjs().tz(timezone).hour(12).minute(0).second(0).millisecond(0);
        const endDefaultTime = defaultStartTime.clone().add(1, 'hour');

        if (item.conditionType === EDisplayConditionType.DATE) {
            if (dateRange[0]) this.defaultStartDate.set(new Date(dateRange[0]));
            if (dateRange[1]) this.defaultEndDate.set(new Date(dateRange[1]));
        }

        if (item.conditionType === EDisplayConditionType.TIME) {
            this.startTime.set(startTime ? dayjs.utc(startTime).tz(value.timeZone) : defaultStartTime);
            this.endTime.set(endTime ? dayjs.utc(endTime).tz(value.timeZone) : endDefaultTime);
        }

        this.defaultConditionDateRange = [startOfDay(new Date()), endOfDay(tomorrow())];
        this.defaultConditionTimeRange = [defaultStartTime.toISOString(), endDefaultTime.toISOString()];

        this.model.set({ conditionType: item.conditionType, equality: item.equality, value: item.value });
    }

    public toDate(date: dayjs.Dayjs): Date {
        return new Date(date.toDate().setHours(date.hour(), date.minute(), 0, 0));
    }

    public setConditionType(conditionType: EDisplayConditionType): void {
        if (conditionType === EDisplayConditionType.ALWAYS) {
            // The parent replaces every condition with the single "always" one.
            this.setOnlyAlwaysCondition.emit();
            return;
        }

        const timeZone = this.maUserTimezone();
        const defaults: Partial<Record<EDisplayConditionType, any>> = {
            [EDisplayConditionType.DATE]: { dateRange: this.defaultConditionDateRange, timeZone },
            [EDisplayConditionType.TIME]: { timeRange: this.defaultConditionTimeRange, timeZone },
            [EDisplayConditionType.URL]: '',
            [EDisplayConditionType.WEEKDAY]: { dayCount: 1, timeZone },
            [EDisplayConditionType.AUTH_EDU]: this.authEduOptions[0],
        };

        if (conditionType === EDisplayConditionType.GROUP_EDU) {
            this.patch({ conditionType, equality: EEqualityType.EQUAL, ...this.eduGroupPatch(this.firstGroup) });
            return;
        }

        this.patch({
            conditionType,
            equality: EEqualityType.EQUAL,
            ...(conditionType in defaults ? { value: defaults[conditionType] } : {}),
        });
    }

    public setEquality(event: Event): void {
        this.patch({ equality: (event.target as HTMLSelectElement).value as EEqualityType });
    }

    public setUrl(event: Event): void {
        const url = (event.target as HTMLInputElement).value;

        if (url) {
            this.patch({ value: url });
        }
    }

    public setAuthEduValue(event: Event): void {
        this.patch({ value: (event.target as HTMLSelectElement).value });
    }

    public setEduGroupValue(group: any): void {
        this.patch(this.eduGroupPatch(group));
    }

    public setStartDate(value: Date): void {
        const start = startOfDay(value);

        this.defaultStartDate.set(start);

        if (start > this.defaultEndDate()) {
            const newEndDate = new Date(start);
            newEndDate.setDate(newEndDate.getDate() + 1);
            this.defaultEndDate.set(endOfDay(newEndDate));
        }

        this.setDateRange();
    }

    public setEndDate(value: Date): void {
        this.defaultEndDate.set(endOfDay(value));
        this.setDateRange();
    }

    public setStartTime(value: Date): void {
        const timeZone = this.maUserTimezone();
        const start = this.startTime().tz(timeZone).hour(value.getHours()).minute(value.getMinutes()).second(0);
        const end = this.endTime().tz(timeZone).hour(this.endTime().hour()).minute(this.endTime().minute()).second(0);

        this.setTimeRange(start, end);
    }

    public setEndTime(value: Date): void {
        const timeZone = this.maUserTimezone();
        const end = this.endTime().tz(timeZone).hour(value.getHours()).minute(value.getMinutes()).second(0);
        const start = this.startTime()
            .tz(timeZone)
            .hour(this.startTime().hour())
            .minute(this.startTime().minute())
            .second(0);

        this.setTimeRange(start, end);
    }

    public setWeekdayValue(event: Event): void {
        // The list starts on Monday, while `dayCount` counts from Sunday.
        const dayCount = ((event.target as HTMLSelectElement).selectedIndex + 1) % 7;

        this.patch({ value: { dayCount, timeZone: this.maUserTimezone() } });
    }

    public removeConditionItem(): void {
        this.removeConditionItemEvent.emit(this.conditionItemIndex());
    }

    private setDateRange(): void {
        this.patch({
            value: { dateRange: [this.defaultStartDate(), this.defaultEndDate()], timeZone: this.maUserTimezone() },
        });
    }

    private setTimeRange(start: dayjs.Dayjs, end: dayjs.Dayjs): void {
        this.startTime.set(start);
        this.endTime.set(end);
        this.patch({
            value: { timeRange: [start.toISOString(), end.toISOString()], timeZone: this.maUserTimezone() },
        });
    }

    private eduGroupPatch(group: any): Partial<ConditionModel> {
        if (!group) {
            return {};
        }

        return {
            value: { groupName: group.name, groupId: group.id, courseName: group.courseName, courseId: group.courseId },
        };
    }

    private patch(patch: Partial<ConditionModel>): void {
        this.model.update((model) => ({ ...model, ...patch }));
        this.valueChangeEvent.emit(this.model() as BlockDisplayConditionItem);
    }

    private getEduCourses$(): Observable<(EduCourseWithTariffs & { groups: any[] })[]> {
        return this.routerFacade.routerParams$.pipe(
            mergeMap((params) => this.eduCourseFacade.eduCoursesWithTariffsBySiteId$(params.siteId)),
            mergeMap((courses) =>
                forkJoin(
                    courses.map((course) =>
                        this.eduCoursesHttpService.getEduCoursesGroups(course.id).pipe(
                            map((groups) =>
                                groups.map((group) => ({
                                    ...group,
                                    courseName: course.name,
                                    courseId: course.id,
                                }))
                            )
                        )
                    )
                )
            ),
            map((groupArrays) => groupArrays.flat()),
            takeUntilDestroyed(this.destroyRef)
        );
    }
}
